import { Suspense, useEffect, useRef } from "react"

import { Navigate, useNavigate } from "react-router-dom"

import { CaptchaPage } from "@/widgets/moderation-settings/captcha/ui/captcha-page"

import { isLeaderboardPeriod } from "@/entities/group/rating"

import { NeuralNetworkLoader } from "@/shared/ui/loading"

import {
  AD_ONBOARDING_PATHS,
  AD_ONBOARDING_TABS,
  buildReportPath,
  buildSubscriptionSurfacePath,
  CELESTIA_PRO_PATHS,
  isFaqDeepLinkTab,
  MODERATION_REPORTS_PATH,
  resolveFaqDeepLinkPath
} from "@/shared/router"
import { WebApp } from "@/shared/telegram"

import { useAppContext } from "../app-provider"

export const LoadingFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-gray-900">
    <NeuralNetworkLoader />
  </div>
)

type ProtectedRouteProps = {
  children: React.ReactNode
  allowMissingChat?: boolean
}

export const ProtectedRoute = ({ children, allowMissingChat = false }: ProtectedRouteProps) => {
  const { chatId, shareToken } = useAppContext()

  if (!chatId && !allowMissingChat && !shareToken) {
    return (
      <Navigate
        to="/dashboard"
        replace
      />
    )
  }

  return children
}

export const DefaultRedirect = () => {
  const { chatId, isInitialized, tabParam, shareToken } = useAppContext()

  // Wait for initialization before redirecting
  if (!isInitialized) {
    return <LoadingFallback />
  }

  // If there are start params, let StandaloneRouteWrapper handle navigation
  if (tabParam || shareToken) {
    return null
  }

  if (chatId) {
    return (
      <Navigate
        to="/moderation"
        replace
      />
    )
  }

  return (
    <Navigate
      to="/dashboard"
      replace
    />
  )
}

export const CaptchaVerificationRoute = () => {
  const { isInitialized } = useAppContext()

  if (!isInitialized) {
    return <LoadingFallback />
  }

  return <CaptchaPage />
}

type StaticDeepLinkTab =
  | "user-profile"
  | "user-settings"
  | "leaderboard"
  | "ai-business-mode"
  | "statistic"
  | "welcome"
  | "clone-create"
  | "api-key"
  | typeof AD_ONBOARDING_TABS.grid
  | typeof AD_ONBOARDING_TABS.business

const STATIC_TAB_ROUTES = {
  "user-profile": "/user-profile",
  "user-settings": "/user-profile",
  leaderboard: "/moderation/level-system/leaderboard",
  "ai-business-mode": CELESTIA_PRO_PATHS.businessMode,
  statistic: "/settings",
  welcome: "/welcome",
  "clone-create": "/celestia-pro/clones/create",
  // Delegatus stores this link as the owner API's key_url; keep the tab and the path stable.
  "api-key": "/user-profile/api-key",
  // Telegram Ads and broadcast links; each onboarding page consumes its own tab.
  [AD_ONBOARDING_TABS.grid]: AD_ONBOARDING_PATHS.grid,
  [AD_ONBOARDING_TABS.business]: AD_ONBOARDING_PATHS.business
} as const satisfies Record<StaticDeepLinkTab, string>

const isStaticDeepLinkTab = (value: string): value is StaticDeepLinkTab => Object.hasOwn(STATIC_TAB_ROUTES, value)

type ChatRequiredDeepLinkTab =
  | "reports"
  | "appeals"
  | "event_journal"
  | "statistics"
  | "captcha"
  | "ai-antispam"
  | "blacklist"
  | "whitelist"
  | "rules-message"
  | "greeting-message"
  | "farewell-message"
  | "auto-messages"
  | "subscription"

const CHAT_REQUIRED_TAB_ROUTES = {
  reports: () => MODERATION_REPORTS_PATH,
  appeals: (_chatId: number, reportId: string | null) =>
    reportId ? buildReportPath(reportId) : MODERATION_REPORTS_PATH,
  event_journal: () => "/moderation",
  statistics: () => "/moderation",
  captcha: () => "/moderation",
  blacklist: () => "/moderation/blacklist",
  whitelist: () => "/moderation",
  "rules-message": () => "/moderation",
  "greeting-message": () => "/moderation",
  "farewell-message": () => "/moderation",
  "auto-messages": () => "/moderation",
  "ai-antispam": () => "/group-settings/ai-antispam",
  subscription: () => buildSubscriptionSurfacePath({ followsChat: true })
} as const satisfies Record<ChatRequiredDeepLinkTab, (chatId: number, reportId: string | null) => string>

const isChatRequiredDeepLinkTab = (value: string): value is ChatRequiredDeepLinkTab =>
  Object.hasOwn(CHAT_REQUIRED_TAB_ROUTES, value)

export const StandaloneRouteWrapper = ({ children }: { children: React.ReactNode }) => {
  const { isInitialized, captchaMode, chatId, tabParam, reportId, shareToken, clearTabParam } = useAppContext()
  const navigate = useNavigate()
  const hasHandledStartParam = useRef(false)

  useEffect(() => {
    if (!isInitialized || captchaMode || (!tabParam && !shareToken) || hasHandledStartParam.current) {
      return
    }
    hasHandledStartParam.current = true

    // /welcome is the only deep-link target mounted outside this wrapper;
    // navigating there unmounts it and loses hasHandledStartParam, so the
    // param must be consumed or every return into "/" re-triggers the jump.
    if (tabParam === "welcome" || tabParam === "clone-create") {
      clearTabParam()
    }

    // The FAQ layout consumes the tab after landing; clearing it here races DefaultRedirect.
    if (tabParam && isFaqDeepLinkTab(tabParam)) {
      void navigate(resolveFaqDeepLinkPath(tabParam), { replace: true })
      return
    }

    if (tabParam && isStaticDeepLinkTab(tabParam)) {
      if (tabParam === "leaderboard") {
        const period = WebApp.initDataUnsafe.start_param
          ?.split("==")
          .find(segment => segment.startsWith("period="))
          ?.split("=")[1]
        const validPeriod = isLeaderboardPeriod(period) ? period : null
        void navigate(
          validPeriod
            ? `/moderation/level-system/leaderboard?period=${validPeriod}`
            : "/moderation/level-system/leaderboard",
          { replace: true }
        )
        return
      }

      void navigate(STATIC_TAB_ROUTES[tabParam], { replace: true })
      return
    }

    if (shareToken) {
      void navigate("/settings", { replace: true })
      return
    }

    if (tabParam && isChatRequiredDeepLinkTab(tabParam)) {
      if (chatId === null) {
        void navigate("/dashboard", { replace: true })
        return
      }
      void navigate(CHAT_REQUIRED_TAB_ROUTES[tabParam](chatId, reportId), { replace: true })
    }
  }, [isInitialized, captchaMode, chatId, tabParam, reportId, shareToken, navigate, clearTabParam])

  if (!isInitialized) {
    return <LoadingFallback />
  }

  if (captchaMode && chatId) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <CaptchaPage />
      </Suspense>
    )
  }

  return children
}
